import { Observable, of } from 'rxjs';
import { shareReplay, switchMap, tap } from 'rxjs/operators';
import { FhirApiService } from '../services/api/fhir-api.sevice';
import { DataFhirModel, DataModel, ResourceOf } from './data.model';

export interface DataCollectionService<Model> {
  /**
   * search Item in collection and if not found fetch it from server. Returns observable item.
   * @param id
   * @returns
   *
   */
  getItemById$(id: string, disableCache?: boolean): Observable<Model>;
}

export abstract class DataCollectionService<Model extends DataModel>
  implements DataCollectionService<Model>
{
  protected collection: { [id: string]: Observable<Model> } = {};
  protected abstract readItemFromAPI$(id: string): Observable<Model>;

  getItemById$(id: string, disableCache?: boolean): Observable<Model> {
    if (disableCache) {
      return this.readItemFromAPI$(id);
    }

    if (!this.collection[id]) {
      this.collection[id] = this.readItemFromAPI$(id).pipe(
        tap((item) => {
          if (item) {
            this.collection[id] = of(item);
          }
        }),
      );
    }

    return this.collection[id];
  }

  clear(): void {
    this.collection = {};
  }
}

export abstract class DataCollectionFhirService<
  Model extends DataFhirModel,
> extends DataCollectionService<Model> {
  protected readonly dataName: string;
  protected readonly resourceType: ResourceOf<Model>['resourceType'];

  constructor(
    dataName: string,
    resourceType: ResourceOf<Model>['resourceType'],
    protected fhirApi: FhirApiService,
  ) {
    super();

    this.dataName = dataName;
    this.resourceType = resourceType;
  }

  protected fromResource$(resource: ResourceOf<Model>): Observable<Model> {
    const entity: Model = {
      id: resource.id,
      _resource: resource,
    } as Model;

    return of(entity);
  }

  protected readItemFromAPI$(id: string): Observable<Model> {
    return this.fhirApi.read(this.resourceType, id, this.dataName).pipe(
      switchMap((resourceItem: ResourceOf<Model>) =>
        this.fromResource$(resourceItem),
      ),
      shareReplay(1),
    );
  }
}
